import { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row, Select } from 'antd';
import { FormProps } from 'antd/es/form/Form';
import { useNavigate } from 'react-router-dom';

// services
import { CreateRegionPayload, onCreateRegion } from 'modules/regions/services/regions';
import NotificationService from 'core/services/notification';
import { onGetAllCountries } from 'modules/countries/services/countries';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	active: 1;
	country_id: number;
};

function CreateRegionPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [countries, setCountries] = useState<{ label: string; value: number }[]>([]);
	const [createForm] = Form.useForm();

	useEffect(() => {
		getCountries();
	}, []);

	const getCountries = async () => {
		const response = await onGetAllCountries();
		setCountries(
			response.data.data.countries.map((el: any) => {
				return { label: el.name, value: el.id };
			}),
		);
		setIsLoading(false);
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createRegionPayload: CreateRegionPayload = {
				name: {
					ar: values['name[ar]'],
					en: values['name[en]'],
				},
				active: 1,
				country_id: values.country_id,
			};

			await onCreateRegion(createRegionPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('REGIONS.CREATED_SUCCESS'));
			navigate('/regions');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('REGIONS.CREATE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('REGIONS.CREATE_TITLE')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('REGIONS.DETAILS')}</h5>

				<Form name="createForm" form={createForm} onFinish={onSubmit}>
					<h6>{t('REGIONS.INFORMATION')}</h6>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('REGIONS.VALIDATION.NAME_EN') }]}>
								<Input size="large" placeholder={t('REGIONS.PLACEHOLDER.NAME_EN')} />
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('REGIONS.VALIDATION.NAME_AR') }]}>
								<Input size="large" placeholder={t('REGIONS.PLACEHOLDER.NAME_AR')} />
							</Form.Item>
						</Col>
					</Row>

					<h6>{t('REGIONS.COUNTRY')}</h6>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="country_id" rules={[{ required: true, message: t('REGIONS.VALIDATION.COUNTRY') }]}>
								<Select
									style={{ width: '100%' }}
									size="large"
									options={countries}
									loading={isLoading}
									showSearch
									filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>

				<Button
					size="middle"
					type="primary"
					htmlType="submit"
					form="createForm"
					loading={isSubmitting}
					style={{ marginTop: 20, display: 'flex' }}>
					{t('COMMON.ADD')}
				</Button>
			</div>
		</>
	);
}

export default CreateRegionPage;
